// The dashboard app SHELL, recreating the prototype natively (brand/ritona-app-handoff index.html/app.css):
// the `.app` grid (sidebar column + main column), the topbar (Ask launcher + Manager/Operator role switch),
// the `.scroll` region carrying the active toolbar-led surface, the persistent chat dock
// (launcher/compact/full states, with the faint compact scrim), the modal mounts (task detail + email
// reader), and the toast surface. It holds only the small client-only view state (current role, active
// surface) plus the cross-cutting shell state machine (useShellState) — NO business state (the dashboard
// owns none, spec §3).
//
// The chat surface is present on EVERY surface (N-CHAT-1): the dock lives at the shell root, so switching
// surfaces never removes it and opening it never changes the surface. The prototype's chat geometry is
// driven by the `.app` classes (chat-open / chat-full) exactly as the handoff CSS specifies. Escape STEPS
// DOWN one level (design brief §4): the most-nested open layer (email → task → chat full → chat compact)
// closes first.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Sidebar } from './Sidebar.tsx';
import { Topbar } from './Topbar.tsx';
import { ChatDock, ChatLauncher } from './ChatDock.tsx';
import { ModalMounts } from './ModalMounts.tsx';
import { Toast } from './Toast.tsx';
import { SurfaceView } from './SurfaceView.tsx';
import { ShellProvider, useShell } from './ShellContext.tsx';
import { OrgDisplayProvider } from './org-display.tsx';
import { ViewerIdentityProvider } from './viewer-identity.tsx';
import { useShellState, type MemoryFocus } from './shell-state.ts';
import { DEFAULT_SURFACE, findSurface, roleCanSeeSurface } from './surfaces.ts';
import type { DashboardRole, SurfaceId } from './surfaces.ts';
import { SurfaceApiProvider } from '../surfaces/SurfaceApiContext.tsx';
import { httpSurfaceApi, type SurfaceApi } from '../surfaces/api-client.ts';
import { WorkApiProvider, httpWorkApi, type WorkApi } from '../surfaces/work/api-client.ts';

export interface AppProps {
  /** The initial role (resolved server-side from the authenticated Actor in a later stage; default MD). */
  readonly initialRole?: DashboardRole;
  /**
   * The surface TRANSPORT the Work surfaces read/route through. The real app supplies the default
   * `httpSurfaceApi()` (fetch against the served Stage-1 endpoints); a UI test injects a fake that returns
   * projection fixtures and records routed intents (hermetic — no real backend network, build constraint).
   */
  readonly api?: SurfaceApi;
  /**
   * The Work-SECTION (WS-J) transport, injected the same way (mirrors the SurfaceApi seam). The real app
   * supplies the default `httpWorkApi()`; a UI test injects the fixture WorkApi — no real network in a UI test.
   */
  readonly workApi?: WorkApi;
}

/** The shell frame — the prototype's `.app` grid + the floating launcher/toast siblings. Rendered inside the
 *  providers so the chat dock and scrim can read the shell state. */
function ShellFrame({
  role,
  active,
  onSelect,
  onRoleChange,
}: {
  readonly role: DashboardRole;
  readonly active: SurfaceId;
  readonly onSelect: (id: SurfaceId) => void;
  readonly onRoleChange: (role: DashboardRole) => void;
}): React.JSX.Element {
  const shell = useShell();
  const surface = findSurface(active) ?? findSurface(DEFAULT_SURFACE)!;
  const appClass =
    'app' +
    (shell.chat !== 'closed' ? ' chat-open' : '') +
    (shell.chat === 'full' ? ' chat-full' : '');

  return (
    <>
      <div className={appClass}>
        <Sidebar role={role} active={active} onSelect={onSelect} />
        <main className="main">
          <Topbar role={role} onRoleChange={onRoleChange} />
          <div className="scroll">
            <SurfaceView surface={surface} role={role} />
          </div>
        </main>
        {/* The faint focus wash behind the COMPACT chat — click-off dismisses (prototype .chat-scrim). */}
        <div className="chat-scrim" aria-hidden="true" onClick={shell.closeChat} />
        <ChatDock />
        <ModalMounts />
        <Toast />
      </div>
      {/* The floating launcher is the `.app` element's SIBLING, exactly as the prototype mounts it. */}
      {shell.chat === 'closed' ? <ChatLauncher onOpen={shell.openChat} /> : null}
    </>
  );
}

/** Read the surface a deep link names (`#<surfaceId>`), or the default. Hash only — the served SPA path
 *  stays `/dashboard`, so a deep link needs no server route (deep-sweep 2026-07-09 F10). An unknown or
 *  out-of-role hash falls back to the default surface (never a blank screen — GI-06). */
function surfaceFromHash(role: DashboardRole): SurfaceId {
  const surface = findSurface(readHash().surfaceId);
  if (surface !== undefined && roleCanSeeSurface(role, surface.id)) return surface.id;
  return DEFAULT_SURFACE;
}

/**
 * Split the deep link into the surface it names and an OPTIONAL focus target within it: `#tasks/tsk_123`
 * opens the Tasks board WITH that Task's detail. The two-segment form exists because the Task-brief email
 * sent at Gate 1 has to land the owner on the exact Task they are being asked to approve — a link to the
 * board alone would make them hunt for it. Still client view-state only: one served path, no server route.
 */
function readHash(): { readonly surfaceId: string; readonly focusId: string | null } {
  const raw = typeof window === 'undefined' ? '' : window.location.hash.replace(/^#/, '');
  const separator = raw.indexOf('/');
  if (separator < 0) return { surfaceId: raw, focusId: null };
  const focus = decodeURIComponent(raw.slice(separator + 1));
  return { surfaceId: raw.slice(0, separator), focusId: focus.length > 0 ? focus : null };
}

/** The per-tab ROLE persistence (deep-sweep 2026-07-16 N6): the toggled role survives a same-tab reload so
 *  a round trip (the OAuth connect return, a hard refresh) re-derives the SAME nav — without it every load
 *  reset to Manager, `#connectors` fell out of scope back to Today, and the `?connect=` outcome marker was
 *  never consumed. Client VIEW-STATE only (sessionStorage, like the chat resume pointer): the role scopes
 *  what the NAV renders; the owning reads scope DATA by the real authenticated Actor regardless (G-180). */
const ROLE_STORAGE_KEY = 'ritona.shell.role';

function readStoredRole(): DashboardRole | null {
  try {
    const raw = window.sessionStorage.getItem(ROLE_STORAGE_KEY);
    return raw === 'admin_md' || raw === 'operator' ? raw : null;
  } catch {
    return null; // storage unavailable — the shell simply starts on the default role
  }
}

function writeStoredRole(role: DashboardRole): void {
  try {
    window.sessionStorage.setItem(ROLE_STORAGE_KEY, role);
  } catch {
    /* storage unavailable — the toggle still works for this load */
  }
}

/**
 * A `#tasks/<taskId>` deep link — the Gate-1 Task-brief email's "Review on dashboard" — opens that Task's
 * detail, so the owner lands on the exact Task they were asked to approve rather than on a board to search.
 * Guarded by the role check, so an out-of-role link opens nothing rather than a modal floating over the
 * fallback surface. Applied on hashchange as well as first load, so following a second link works too.
 */
function useTaskDeepLink(role: DashboardRole, openTaskModal: (id: string) => void): void {
  useEffect(() => {
    const applyTaskFocus = (): void => {
      const { surfaceId, focusId } = readHash();
      if (surfaceId !== 'tasks' || focusId === null) return;
      if (!roleCanSeeSurface(role, 'tasks')) return;
      openTaskModal(focusId);
    };
    applyTaskFocus();
    window.addEventListener('hashchange', applyTaskFocus);
    return () => window.removeEventListener('hashchange', applyTaskFocus);
  }, [role, openTaskModal]);
}

/**
 * A clickable memory activity row (BUILD-DEC-098) requests a Memory-browser focus; switch to that surface
 * when it is reachable in the current role. The Memory surface applies the focus then clears it. Lifted out
 * of `App` alongside the other two shell effects (the deep link, Escape) so the root component stays the
 * composition it is meant to be rather than a pile of effects.
 */
function useMemoryFocusSurface(
  memoryFocus: MemoryFocus | null,
  role: DashboardRole,
  setActive: (id: SurfaceId) => void,
): void {
  useEffect(() => {
    if (memoryFocus !== null && roleCanSeeSurface(role, 'memory')) setActive('memory');
  }, [memoryFocus, role, setActive]);
}

/** Escape steps down one level of whichever surfaces are open (email → task → chat full → compact). */
function useEscapeStepsDown(stepDown: () => void): void {
  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') stepDown();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [stepDown]);
}

/**
 * The composed dashboard shell.
 *
 * The two root-level display reads feed shell chrome rather than a Surface: `OrgDisplayProvider` reads this
 * box's configured org name/meta, while `ViewerIdentityProvider` reads the exact authenticated human's
 * display-only Identity projection. Both sit inside the injected transport provider so UI tests stay
 * hermetic. Org has a harmless branding fallback; viewer identity stays neutral until its trusted read lands.
 */
export function App({ initialRole = 'admin_md', api, workApi }: AppProps): React.JSX.Element {
  // Default to the real HTTP transport; memoise so the surfaces' `load` deps stay stable across renders.
  const surfaceApi = useMemo<SurfaceApi>(() => api ?? httpSurfaceApi(), [api]);
  const workTransport = useMemo<WorkApi>(() => workApi ?? httpWorkApi(), [workApi]);
  // The role restores from the per-tab store (N6) so a reload/OAuth-return keeps the operator's nav scope.
  const [role, setRole] = useState<DashboardRole>(() => readStoredRole() ?? initialRole);
  // Deep-linkable navigation (F10): the active surface initialises from the URL hash, every selection
  // PUSHES a history entry (`#<surfaceId>`), and popstate (browser Back/Forward) steps between surfaces
  // instead of leaving the app. Client view-state only — no business state, no server route. The hash is
  // validated against the RESTORED role (N6) so `#connectors` survives an operator's round trip.
  const [active, setActiveState] = useState<SurfaceId>(() => surfaceFromHash(role));
  const setActive = useCallback((id: SurfaceId) => {
    setActiveState((current) => {
      if (id !== current && typeof window !== 'undefined') {
        window.history.pushState({ surface: id }, '', `#${id}`);
      }
      return id;
    });
  }, []);
  const shell = useShellState();

  // Browser Back/Forward re-derives the surface from the hash (in-app history — GI-06/F10). The
  // hashchange leg covers IN-SURFACE navigation (a surface assigning `location.hash` — e.g. Today's
  // Approvals banner), which pushes its own history entry without firing popstate in every browser.
  useEffect(() => {
    const onHash = (): void => setActiveState(surfaceFromHash(role));
    window.addEventListener('popstate', onHash);
    window.addEventListener('hashchange', onHash);
    return () => {
      window.removeEventListener('popstate', onHash);
      window.removeEventListener('hashchange', onHash);
    };
  }, [role]);

  useEscapeStepsDown(shell.stepDown);

  // On a role switch, if the active surface is not reachable in the new role, fall back to the default.
  // The switch persists per-tab (N6) so the nav scope survives a reload / the OAuth connect round trip.
  const changeRole = useCallback((next: DashboardRole) => {
    writeStoredRole(next);
    setRole(next);
    setActiveState((current) => (roleCanSeeSurface(next, current) ? current : DEFAULT_SURFACE));
  }, []);

  useTaskDeepLink(role, shell.openTaskModal);

  useMemoryFocusSurface(shell.memoryFocus, role, setActive);

  return (
    <SurfaceApiProvider value={surfaceApi}>
      <WorkApiProvider value={workTransport}>
        <OrgDisplayProvider>
          <ViewerIdentityProvider>
            <ShellProvider value={shell}>
              <ShellFrame
                role={role}
                active={active}
                onSelect={setActive}
                onRoleChange={changeRole}
              />
            </ShellProvider>
          </ViewerIdentityProvider>
        </OrgDisplayProvider>
      </WorkApiProvider>
    </SurfaceApiProvider>
  );
}
